import {
  Button,
  Input,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  DismissRegular,
  SaveRegular,
  SendRegular,
} from '@fluentui/react-icons';
import { Controller, useForm } from 'react-hook-form';
import {
  VisibilityRolesFields,
  type CommentFormValues,
} from './VisibilityRolesFields';

interface CommentFormProps {
  mode: 'new' | 'edit';
  defaultValues: CommentFormValues;
  submitting: boolean;
  disabled: boolean;
  onSubmit: (values: CommentFormValues) => Promise<boolean>;
  onCancel?: () => void;
}

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  input: { flexGrow: 1, minWidth: 0 },
});

export const CommentForm = ({
  mode,
  defaultValues,
  submitting,
  disabled,
  onSubmit,
  onCancel,
}: CommentFormProps) => {
  const classes = useStyles();
  const { control, handleSubmit, watch, reset } = useForm<CommentFormValues>({
    defaultValues,
  });

  const [content, isPublic, roles] = watch(['content', 'isPublic', 'roles']);
  const rolesValid = !isPublic || roles.length > 0;
  const canSubmit = content.trim().length > 0 && rolesValid && !disabled;

  const submit = handleSubmit(async (values) => {
    const ok = await onSubmit(values);
    if (ok && mode === 'new') reset(defaultValues);
  });

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing && canSubmit) {
      event.preventDefault();
      submit();
    }
  };

  const primaryIcon = submitting ? (
    <Spinner size="tiny" />
  ) : mode === 'new' ? (
    <SendRegular />
  ) : (
    <SaveRegular />
  );

  return (
    <div className={classes.form}>
      <div className={classes.inputRow}>
        <Controller
          control={control}
          name="content"
          render={({ field }) => (
            <Input
              autoFocus={mode === 'edit'}
              placeholder={mode === 'new' ? 'Write a comment…' : undefined}
              className={classes.input}
              value={field.value}
              disabled={disabled}
              onChange={(_ev, data) => field.onChange(data.value)}
              onKeyDown={handleKeyDown}
            />
          )}
        />
        <Button
          aria-label={mode === 'new' ? 'Send comment' : 'Save'}
          appearance="primary"
          icon={primaryIcon}
          disabled={!canSubmit}
          onClick={submit}
        />
        {mode === 'edit' && (
          <Button
            aria-label="Cancel"
            icon={<DismissRegular />}
            disabled={disabled}
            onClick={onCancel}
          />
        )}
      </div>

      <VisibilityRolesFields control={control} disabled={disabled} />
    </div>
  );
};
